import { HttpTypes } from "@medusajs/types"

// Krek warehouse constants
export const KREK_WAREHOUSE_ID = "sloc_01KGH5WRSB4VN0CNP3FERXMWXH"
export const KREK_WAREHOUSE_NAME = "Krek"

// Warehouse configuration interface
export interface WarehouseInfo {
  id: string
  name: string
  dobavni_rok: number // delivery days for shipping
  pickup_days?: number // pickup availability (0 = today)
  address?: {
    address_1: string
    city: string
    phone?: string
  }
}

// Known warehouses with their delivery times (fallback if dynamic metadata fails)
export const WAREHOUSES: Record<string, WarehouseInfo> = {
  [KREK_WAREHOUSE_ID]: {
    id: KREK_WAREHOUSE_ID,
    name: "Krek",
    dobavni_rok: 3, // 2 days from database (was hardcoded as 3)
    pickup_days: 0, // 0 days = pickup available today
    address: {
      address_1: "Hraska cesta 15",
      city: "Lesce",
      phone: "045302344"
    }
  },
  "sloc_01K3627MH9S5D2QX481AB0S5E5": {
    id: "sloc_01K3627MH9S5D2QX481AB0S5E5",
    name: "Hendi Austria",
    dobavni_rok: 10 // 10 days from database
  }
}

/**
 * Safely parse a value that could be a number or numeric string.
 * Returns fallback when the value is null, undefined, or not a valid number.
 */
function safeParseInt(value: unknown, fallback: number): number {
  if (value == null) return fallback
  const n = typeof value === "number" ? value : parseInt(String(value), 10)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Read dobavni_rok from a warehouse info/metadata object.
 * The API endpoint extracts it to a top-level field, but the raw
 * Medusa stock-location entity stores it inside `metadata.dobavni_rok`.
 * This helper checks both locations so neither path silently fails.
 */
function getDobavniRok(info: Record<string, any> | undefined | null, fallback: number): number {
  if (!info) return fallback
  const raw = info.dobavni_rok ?? info.metadata?.dobavni_rok
  return safeParseInt(raw, fallback)
}

/**
 * Same dual-path lookup for pickup_days.
 */
function getPickupDays(info: Record<string, any> | undefined | null, fallback?: number): number | undefined {
  if (!info) return fallback
  const raw = info.pickup_days ?? info.metadata?.pickup_days
  if (raw == null) return fallback
  return safeParseInt(raw, fallback ?? 0)
}

// Stock information for a warehouse
export interface WarehouseStock {
  warehouseId: string
  warehouseName: string
  availableQuantity: number
  deliveryDays: number
  pickupDays?: number // for warehouses that support pickup
  canPickup: boolean
}

// Delivery estimate result
export interface DeliveryEstimate {
  krekStock: WarehouseStock | null
  alternativeWarehouses: WarehouseStock[]
  totalAvailable: number
  estimatedDelivery: {
    date: Date
    message: string
    pickupMessage?: string
  }
}

/**
 * Get Krek warehouse stock for a product
 * This is a sync function that needs warehouse data passed from server component
 */
export function getKrekStock(product: HttpTypes.StoreProduct, warehouseStock?: Record<string, number>): number {
  if (!product?.variants || !product.id) return 0

  // Use warehouse stock data passed from server component
  if (warehouseStock) {
    return warehouseStock[KREK_WAREHOUSE_ID] || 0
  }

  // Fallback to using the total inventory from Store API
  // This is a reasonable approximation when detailed warehouse data isn't available
  let totalStock = 0
  product.variants.forEach(variant => {
    if (variant.manage_inventory === false) {
      totalStock += 999 // Unmanaged inventory
    } else {
      totalStock += variant.inventory_quantity || 0
    }
  })

  // Conservative estimate: assume 30% might be in Krek
  return Math.min(Math.floor(totalStock * 0.3), 5)
}

/**
 * Get all warehouse stock information for a product
 * Uses warehouse stock data passed from server component
 */
export function getAllWarehouseStock(
  product: HttpTypes.StoreProduct, 
  warehouseStock?: Record<string, number>,
  warehouseMetadata?: Record<string, any>
): WarehouseStock[] {
  if (!product?.variants || !product.id) return []

  const warehouses: WarehouseStock[] = []

  if (warehouseStock) {
    const krekStock = warehouseStock[KREK_WAREHOUSE_ID] || 0
    const krekMeta = warehouseMetadata?.[KREK_WAREHOUSE_ID]
    const krekFallback = WAREHOUSES[KREK_WAREHOUSE_ID]

    warehouses.push({
      warehouseId: KREK_WAREHOUSE_ID,
      warehouseName: krekMeta?.name ?? krekFallback?.name ?? "Krek",
      availableQuantity: krekStock,
      deliveryDays: getDobavniRok(krekMeta, krekFallback?.dobavni_rok ?? 3),
      pickupDays: getPickupDays(krekMeta, krekFallback?.pickup_days ?? 0),
      canPickup: true
    })
    
    Object.entries(warehouseStock).forEach(([warehouseId, quantity]) => {
      if (warehouseId !== KREK_WAREHOUSE_ID && quantity > 0) {
        const meta = warehouseMetadata?.[warehouseId]
        const fallback = WAREHOUSES[warehouseId]

        warehouses.push({
          warehouseId,
          warehouseName: meta?.name ?? fallback?.name ?? warehouseId,
          availableQuantity: quantity,
          deliveryDays: getDobavniRok(meta, fallback?.dobavni_rok ?? 30),
          pickupDays: getPickupDays(meta, fallback?.pickup_days),
          canPickup: false
        })
      }
    })
  } else {
    // Fallback: distribute total inventory across known warehouses
    let totalStock = 0
    product.variants.forEach(variant => {
      if (variant.manage_inventory === false) {
        totalStock += 999
      } else {
        totalStock += variant.inventory_quantity || 0
      }
    })

    if (totalStock > 0) {
      // Conservative Krek estimate
      const krekStock = Math.min(Math.floor(totalStock * 0.3), 5)
      if (krekStock > 0) {
        warehouses.push({
          warehouseId: KREK_WAREHOUSE_ID,
          warehouseName: "Krek",
          availableQuantity: krekStock,
          deliveryDays: 2, // 2 days for shipping
          pickupDays: 0, // Available today for pickup
          canPickup: true
        })
      }

      // Remaining goes to Hendi Austria
      const remainingStock = totalStock - krekStock
      if (remainingStock > 0) {
        warehouses.push({
          warehouseId: "sloc_01K3627MH9S5D2QX481AB0S5E5",
          warehouseName: "Hendi Austria",
          availableQuantity: remainingStock,
          deliveryDays: 10,
          pickupDays: undefined, // No pickup available
          canPickup: false
        })
      }
    }
  }

  return warehouses.sort((a, b) => {
    // Krek first, then by delivery time
    if (a.warehouseId === KREK_WAREHOUSE_ID) return -1
    if (b.warehouseId === KREK_WAREHOUSE_ID) return 1
    return a.deliveryDays - b.deliveryDays
  })
}

/**
 * Calculate delivery estimate for requested quantity
 */
export function calculateDeliveryEstimate(
  product: HttpTypes.StoreProduct, 
  requestedQuantity: number = 1,
  warehouseStock?: Record<string, number>,
  warehouseMetadata?: Record<string, any> // Dynamic warehouse metadata from API
): DeliveryEstimate {
  const allStock = getAllWarehouseStock(product, warehouseStock, warehouseMetadata)
  const krekStock = allStock.find(stock => stock.warehouseId === KREK_WAREHOUSE_ID)
  const alternativeWarehouses = allStock.filter(stock => stock.warehouseId !== KREK_WAREHOUSE_ID)
  
  const totalAvailable = allStock.reduce((sum, stock) => sum + stock.availableQuantity, 0)
  
  // Calculate delivery based on availability
  let estimatedDelivery: DeliveryEstimate['estimatedDelivery']

  if (!krekStock && alternativeWarehouses.length === 0) {
    // No stock available
    estimatedDelivery = {
      date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days default
      message: "Trenutno ni na zalogi"
    }
  } else if (krekStock && krekStock.availableQuantity >= requestedQuantity) {
    // Full quantity available in Krek
    const deliveryDate = new Date(Date.now() + krekStock.deliveryDays * 24 * 60 * 60 * 1000)
    const pickupDays = krekStock.pickupDays || 0
    const isPickupToday = pickupDays === 0
    const now = new Date()
    const currentHour = now.getHours()
    const currentDay = now.getDay() // 0 is Sunday, 6 is Saturday

    let pickupMessageContent = ""
    if (isPickupToday) {
      if (currentDay === 6) { // Saturday
        pickupMessageContent = "Prevzem možen v ponedeljek od 8:00 do 16:00"
      } else if (currentDay === 0) { // Sunday
        pickupMessageContent = "Prevzem možen v ponedeljek od 8:00 do 16:00"
      } else if (currentHour >= 16) {
        if (currentDay === 5) { // Friday after 16:00
          pickupMessageContent = "Prevzem možen v ponedeljek od 8:00 do 16:00"
        } else {
          pickupMessageContent = "Prevzem možen jutri od 8:00 do 16:00"
        }
      } else {
        pickupMessageContent = "Prevzem možen danes do 16:00"
      }
    } else {
      pickupMessageContent = `Prevzem možen v ${pickupDays} dneh`
    }
    
    estimatedDelivery = {
      date: deliveryDate,
      message: `Dostava v ${krekStock.deliveryDays} dneh`,
      pickupMessage: pickupMessageContent
    }
  } else if (krekStock && krekStock.availableQuantity > 0) {
    // Partial stock in Krek, need alternative warehouse
    const remainingQuantity = requestedQuantity - krekStock.availableQuantity
    const bestAlternative = alternativeWarehouses.find(w => w.availableQuantity >= remainingQuantity)
    
    if (bestAlternative) {
      const deliveryDate = new Date(Date.now() + bestAlternative.deliveryDays * 24 * 60 * 60 * 1000)
      const krekPickupDays = krekStock.pickupDays || 0
      const isPickupToday = krekPickupDays === 0
      const now = new Date()
      const currentHour = now.getHours()
      const currentDay = now.getDay()
      
      let pickupMessageContent = ""
      if (isPickupToday) {
          if (currentDay === 6) { // Saturday
              pickupMessageContent = `${krekStock.availableQuantity} kosov na prevzem v ponedeljek`
          } else if (currentDay === 0) { // Sunday
              pickupMessageContent = `${krekStock.availableQuantity} kosov na prevzem v ponedeljek`
          } else if (currentHour >= 16) {
              if (currentDay === 5) { // Friday after 16:00
                  pickupMessageContent = `${krekStock.availableQuantity} kosov na prevzem v ponedeljek`
              } else {
                  pickupMessageContent = `${krekStock.availableQuantity} kosov na prevzem jutri`
              }
          } else {
              pickupMessageContent = `${krekStock.availableQuantity} kosov na prevzem danes`
          }
      } else {
          pickupMessageContent = `${krekStock.availableQuantity} kosov na prevzem v ${krekPickupDays} dneh`
      }

      estimatedDelivery = {
        date: deliveryDate,
        message: `${krekStock.availableQuantity} kosov v ${krekStock.deliveryDays} dneh, ${remainingQuantity} kosov v ${bestAlternative.deliveryDays} dneh`,
        pickupMessage: pickupMessageContent
      }
    } else {
      // Not enough total stock
      const deliveryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      estimatedDelivery = {
        date: deliveryDate,
        message: `Samo ${totalAvailable} kosov na voljo`
      }
    }
      } else {
      // No Krek stock, use best alternative
      const bestAlternative = alternativeWarehouses[0]
      if (bestAlternative) {
        const deliveryDate = new Date(Date.now() + bestAlternative.deliveryDays * 24 * 60 * 60 * 1000)
        estimatedDelivery = {
          date: deliveryDate,
          message: `Dostava v ${bestAlternative.deliveryDays} dneh` // Removed warehouse name
        }
      } else {
        const deliveryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        estimatedDelivery = {
          date: deliveryDate,
          message: "Trenutno ni na zalogi"
        }
      }
    }

  return {
    krekStock: krekStock || null,
    alternativeWarehouses,
    totalAvailable,
    estimatedDelivery
  }
}

/**
 * Format delivery date for display
 */
export function formatDeliveryDate(date: Date, alwaysShowDate: boolean = false): string {
  const today = new Date()
  const diffTime = date.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  // If alwaysShowDate is true, show the actual date for any future date
  if (alwaysShowDate && diffDays > 0) {
    return date.toLocaleDateString('sl-SI', { 
      day: 'numeric', 
      month: 'long',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    })
  }

  if (diffDays === 0) return "danes"
  if (diffDays === 1) return "jutri"
  if (diffDays <= 7) return `v ${diffDays} dneh`
  
  return date.toLocaleDateString('sl-SI', { 
    day: 'numeric', 
    month: 'long',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
  })
}

/**
 * Check if pickup is available today
 */
export function getPickupAvailability(): { available: boolean; message: string } {
  const now = new Date()
  const currentHour = now.getHours()
  const isWeekend = now.getDay() === 0 || now.getDay() === 6 // Sunday or Saturday

  if (isWeekend) {
    return {
      available: false,
      message: "Prevzem možen od ponedeljka do petka, 8:00-16:00"
    }
  }

  if (currentHour < 8) {
    return {
      available: false,
      message: "Prevzem možen danes od 8:00 do 16:00"
    }
  }

  if (currentHour >= 16) {
    return {
      available: false,
      message: "Prevzem možen jutri od 8:00 do 16:00"
    }
  }

  return {
    available: true,
    message: `Prevzem možen danes do 16:00`
  }
}
